import { BalanceData, LevelModifiers, RunProfile, challengeProfile, challengeSeed, dailySeed, decodeChallengeCode, normalisedProfile, raceSeed } from "@highmarch/domain";
import type { LevelCatalog } from "./levelCatalog";
import { Boosts } from "./boosts";

/**
 * The fair-mode switch (MP-32) as a use case: what a run starts from. A campaign, Heroic, Trial, Iron or Endless run
 * takes the player's save (owned towers, tower and Fortress levels, `PlayerProfile.gearBonuses()` — gear, VIP combat
 * and Gold Research); a run of a `normalised` mode takes the data's profile (domain meta/fairProfile.ts) and nothing
 * from the save but the player's VIP combat bonus, when the data allows it (`normalised.allowVip`, owner D1). A mode
 * with a fixed seed (Daily Road: the day's; a challenge code: the code's) also fixes the seed, so every player's run is
 * the same. A challenge code's run (MP-28) also deals only the code's towers. Race Week (MP-21) fixes only the seed
 * (a normal run otherwise: D1). Future fair modes (Expedition) add their rule to `isNormalisedRun`/`fixedSeed`.
 */

/** Whether `modifiers` are a run of a normalised mode (modes.json `<mode>.normalised`). */
export function isNormalisedRun(balance: BalanceData, modifiers: LevelModifiers | null): boolean {
  return (modifiers?.daily !== undefined && balance.modes.daily.normalised) || (modifiers?.challenge !== undefined && balance.modes.challenge.normalised);
}

/** The challenge a run's modifiers carry (its code decoded), or null. */
function runChallenge(modifiers: LevelModifiers | null) {
  if (!modifiers?.challenge) return null;
  const r = decodeChallengeCode(modifiers.challenge.code);
  return r.ok ? r.challenge : null;
}

/** The seed every player's run of these modifiers shares, or null for a run seeded per player and attempt. */
export function fixedRunSeed(modifiers: LevelModifiers | null): bigint | null {
  const c = runChallenge(modifiers);
  if (modifiers?.daily !== undefined) return dailySeed(modifiers.daily.day);
  if (c) return challengeSeed(c);
  // MP-21 Race Week: one road and one seed a week, the same for every player and every try (a normal run otherwise: D1).
  return modifiers?.event?.mode === "race" ? raceSeed(modifiers.event.week) : null;
}

/**
 * The player's own profile: what a campaign run is built from. `now` (a battle starting): the battle boosts running
 * then are added to its gear (owner 2026-10-21, app boosts.ts); without it, the gear alone.
 */
export function playerProfile(catalog: LevelCatalog, now: number | null = null): RunProfile {
  const gear = catalog.profile.gearBonuses();
  return { owned: catalog.ownedTowers(), towerLevels: catalog.towerLevels(), gear: now === null ? gear : new Boosts(catalog).gearFor(gear, now) };
}

/** What a run of `modifiers` starts from: the normalised profile (with the player's VIP combat) for a fair mode, else the player's. */
export function runProfileFor(catalog: LevelCatalog, modifiers: LevelModifiers | null, now: number | null = null): RunProfile {
  const c = runChallenge(modifiers);
  if (c && isNormalisedRun(catalog.balance, modifiers)) return challengeProfile(catalog.balance, c, catalog.profile.vip);
  return isNormalisedRun(catalog.balance, modifiers) ? normalisedProfile(catalog.balance, catalog.profile.vip) : playerProfile(catalog, now);
}
